<template>
  <div class="section container">
    <div class="section-header">
      <div class="section-title">
        <div class="section-icon section-icon-address" :class="brandClass()"></div>
        <h3 class="section-title-label">
          {{$t("profile_personal_data_address_summary_title")}}
        </h3>
        <div class="section-subtitle-label">
          {{$t("profile_personal_data_address_summary_subtitle")}}
        </div>
      </div> 
    </div>
    
    <div class="infos addresses-information">

      <!-- For each address typology... -->
      <div 
        v-for="(userAddressStatus, userAddressStatusIndex) in getUserAddressStatuses()" 
        :key="userAddressStatusIndex"
        class="info"
        style="width: 100%">

        <!-- Address status title -->
        <div class="address-status-label label" :class="brandClass()">
          {{ $t(userAddressStatus.translationKey) }}
        </div>

        <!-- If the user has at least one address... -->
        <template v-if="userAddresses()?.length">

          <!-- For each address in the current typology... -->
          <div 
            v-for="(filteredAddressObject, filteredAddressObjectIndex) in getFirstAddress(userAddressStatus.userFilteredAddresses)" 
            class="info-container address-container" 
            :class="{
              'display_none':filteredAddressObject.userAddress.delete, 
              'opaque':(indexToEdit!=null && indexToEdit !== filteredAddressObject.userAddressIndex)
            }"
            :key="`address-${filteredAddressObject.userAddressIndex}`">

            <div 
              v-if="!filteredAddressObject.userAddress.delete" 
              class="info address" 
              style="height: 100%">

              <!-- Address values and preferred radio container -->
              <div style="display: flex; margin-bottom: 1rem">

                <!-- Radio column -->
                <div class="radio-column">

                  <!-- Preferred radio -->
                  <div 
                    @keydown="ariaClick" 
                    role="button" 
                    tabindex="0" 
                    @click="
                      !editDisabled && 
                      (editMode === ACTION.VIEW || editMode === ACTION.ADD) &&
                      handlePreferredAddress(filteredAddressObject)" 
                    :aria-label="$t('preferred')"
                    class="radio" 
                    :class="{
                      'pointer': (editMode === ACTION.VIEW || editMode === ACTION.ADD),
                      'selected': filteredAddressObject.userAddress.preferred
                    }" 
                  />
                </div>

                <!-- Address values column -->
                <div 
                  class="address-value-list"
                  :class="[
                    filteredAddressObject.userAddress.addressStatus=='BUSINESS' ? 
                      'business-address-value-list' : 
                      'private-address-value-list',
                    filteredAddressObject.userAddress.addressStatus=='BUSINESS' && filteredAddressObject.userAddress.companyName ? 
                      'has-company-name' :
                      'has-no-company-name'
                  ]">

                  <!-- Address values -->

                  <!-- <div :style="{order:0}" class="label address-label">{{statusLabel(address.addressStatus)}}</div> -->
                  <div :style="{order:getFieldOrder('street', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'street')">{{displayUserAddressData(filteredAddressObject.userAddress, "street")}}</div>
                  <div :style="{order:getFieldOrder('houseNumber', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'houseNumber')">{{displayUserAddressData(filteredAddressObject.userAddress, "houseNumber")}}</div>
                  <div :style="{order:getFieldOrder('strSuppl1', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'strSuppl1')">{{displayUserAddressData(filteredAddressObject.userAddress, "strSuppl1")}}</div>
                  <div :style="{order:getFieldOrder('strSuppl2', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'strSuppl2')">{{displayUserAddressData(filteredAddressObject.userAddress, "strSuppl2")}}</div>
                  <div :style="{order:getFieldOrder('postalCode', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'postalCode')">{{displayUserAddressData(filteredAddressObject.userAddress, "postalCode")}}</div>
                  <div :style="{order:getFieldOrder('city', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'city')">{{displayUserAddressData(filteredAddressObject.userAddress, "city")}}</div>
                  <div :style="{order:getFieldOrder('regionCode', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'regionCode')">{{displayUserAddressData(filteredAddressObject.userAddress, "regionCode")}}</div>
                  <div :style="{order:getFieldOrder('country', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'country')">{{displayUserAddressData(filteredAddressObject.userAddress, "country")}}</div>
                  <div :style="{order:getFieldOrder('companyName', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="filteredAddressObject.userAddress.addressStatus==='BUSINESS' && isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'companyName')">{{displayUserAddressData(filteredAddressObject.userAddress, "companyName")}}</div>
                  <div :style="{order:getFieldOrder('postOfficeBox', filteredAddressObject.userAddress.country)}" class="value address-value" v-if="filteredAddressObject.userAddress.addressStatus==='BUSINESS' && isUserAddressDataToDisplay(filteredAddressObject.userAddress, 'postOfficeBox')">{{displayUserAddressData(filteredAddressObject.userAddress, "postOfficeBox")}}</div>
                </div>
              </div>

              <!-- Edit and delete buttons container -->
              <div style="display: flex; align-items: end; flex: 1 1 auto;">

                <!-- Edit button -->
                <div class="dashboardContainer" :class="{'hidden': indexToEdit === filteredAddressObject.userAddressIndex || editMode === ACTION.ADD}">
                  <div 
                    :id="filteredAddressObject.userAddress.addressStatus?.toLowerCase() + '-address-' + filteredAddressObjectIndex + '-edit-button'" 
                    @keydown="ariaClick" 
                    role="button" 
                    tabindex="0" 
                    class="edit" 
                    :class="{'pointer': editMode === ACTION.VIEW, 'hidden': editDisabled}" 
                    :aria-label="$t('profile_edit_button')"
                    @click="editMode === ACTION.VIEW && handleEditMode(filteredAddressObject.userAddressIndex, undefined, filteredAddressObject.userAddress.addressStatus?.toLowerCase() + '-address-' + filteredAddressObjectIndex + '-edit-button')">
                  </div>
                  <span class="buttonLabel" :class="{'pointer': editMode === ACTION.VIEW, 'hidden': editDisabled}" @click="editMode === ACTION.VIEW && handleEditMode(filteredAddressObject.userAddressIndex, undefined, filteredAddressObject.userAddress.addressStatus?.toLowerCase() + '-address-' + filteredAddressObjectIndex + '-edit-button')">
                    {{$t('profile_edit_button')}}
                  </span>
                </div>

                <!-- Delete button -->
                <div class="dashboardContainer" :class="{'hidden': indexToEdit === filteredAddressObject.userAddressIndex || editMode === ACTION.ADD}">
                  <div 
                    :id="filteredAddressObject.userAddress.addressStatus?.toLowerCase() + '-address-' + filteredAddressObjectIndex + '-delete-button'" 
                    @keydown="ariaClick" 
                    role="button" 
                    tabindex="0" 
                    class="delete" 
                    :class="{'pointer': editMode === ACTION.VIEW, 'hidden': editDisabled}" 
                    :aria-label="$t('profile_delete_button')"
                    @click="editMode === ACTION.VIEW && openPopup(filteredAddressObject, filteredAddressObject.userAddress.addressStatus?.toLowerCase() + '-address-' + filteredAddressObjectIndex + '-delete-button')">
                  </div>
                  <span class="buttonLabel" :class="{'pointer': editMode === ACTION.VIEW, 'hidden': editDisabled}" @click="editMode === ACTION.VIEW && openPopup(filteredAddressObject, filteredAddressObject.userAddress.addressStatus?.toLowerCase() + '-address-' + filteredAddressObjectIndex + '-delete-button')">
                    {{$t('profile_personal_data_address_summary_remove_button')}}
                  </span>
                </div>

                <!-- Preferred address indicator (replaced by the preferred radio button above) -->
                <!-- <div v-if="address.preferred" class="value address-value">{{$t('profile_personal_data_address_summary_preferred_address')}}</div> -->
              </div>
            </div>
          </div>
        </template>

        <!-- Add address button -->
        <div 
          v-if="isAddButtonVisible(userAddressStatus)"
          id="add" 
          class="add pointer" 
          @click="handleEditMode(ACTION.ADD, userAddressStatus.key, userAddressStatus.key?.toLowerCase() + '-address-add-button')">
          <div 
            :id="userAddressStatus.key?.toLowerCase() + '-address-add-button'" 
            @keydown="ariaClick" 
            role="button" 
            tabindex="0" 
            class="add-icon" 
            :class="brandClass()" 
            :aria-label="$t('profile_personal_data_address_summary_add_address')"
            @click="handleEditMode(ACTION.ADD, userAddressStatus.key, userAddressStatus.key?.toLowerCase() + '-address-add-button')" 
          />
          <div class="add-label">
            {{$t("profile_personal_data_address_summary_add_address")}}
          </div>
        </div>

        <!-- Add / edit form -->
        <ProfileAddressesForm 
          v-if="editMode !== ACTION.VIEW && form.type == userAddressStatus.key" 
          :form="form"
          :editMode="editMode"
          @onCancelButtonClick="cancelClicked()"
          @onApplyButtonClick="validateAddress"
        />

      </div>
    </div>

    <!-- Add / edit form -->
    <!-- <ProfileAddressesForm 
      v-if="editMode !== ACTION.VIEW" 
      :form="form"
      :editMode="editMode"
      @onCancelButtonClick="cancelClicked()"
      @onApplyButtonClick="validateAddress()"
    /> -->

    <OneAlert
      :brandClass="brandClass()"
      v-if="showPopup"
      :title="$t('profile_personal_data_delete_address_title')"
      :content="$t('profile_personal_data_delete_address_subtitle')"
      :button_confirm_label="$t('profile_delete_button')"
      :button_cancel_label="$t('profile_personal_data_delete_address_cancel_button')"
      @confirm="remove"
      @close="closePopup"
    >
    </OneAlert>
    <OneAlert
      :brandClass="brandClass()"
      v-if="isSuggestedAddressModalVisible"
      :title="$t('profile_personal_data_address_suggest_title')"
      :content="$t('profile_personal_data_address_suggest_subtitle')"
      :button_confirm_label="$t('profile_personal_data_address_suggest_apply_button')"
      :button_cancel_label="''"
      :button_big_label="''"
      @confirm="addressSuggestionManagement(true)"
      :showCloseButton="false"
    >
      <template v-slot:internal-content>
        <div class="info-container address-container">
          <div class="address-title">{{$t('profile_personal_data_address_suggest_label')}}</div>
          <div class="info address">
            <div :style="{order:getFieldOrder('street', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'street')">{{displayUserAddressData(suggestedAddress, "street")}}</div>
            <div :style="{order:getFieldOrder('houseNumber', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'houseNumber')">{{displayUserAddressData(suggestedAddress, "houseNumber")}}</div>
            <div :style="{order:getFieldOrder('strSuppl2', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'strSuppl2')">{{displayUserAddressData(suggestedAddress, "strSuppl2")}}</div>
            <div :style="{order:getFieldOrder('strSuppl1', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'strSuppl1')">{{displayUserAddressData(suggestedAddress, "strSuppl1")}}</div>
            <div :style="{order:getFieldOrder('postalCode', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'postalCode')">{{displayUserAddressData(suggestedAddress, "postalCode")}}</div>
            <div :style="{order:getFieldOrder('city', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'city')">{{displayUserAddressData(suggestedAddress, "city")}}</div>
            <div :style="{order:getFieldOrder('regionCode', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'regionCode')">{{displayUserAddressData(suggestedAddress, "regionCode")}}</div>
            <div :style="{order:getFieldOrder('country', suggestedAddress.country)}" class="value address-value" v-if="isUserAddressDataToDisplay(suggestedAddress, 'country')">{{displayUserAddressData(suggestedAddress, "country")}}</div>
          </div>
        </div>
      </template>
      <template v-slot:external-content>
        <div class="discard-label" :class="brandClass()">
          <span @keydown="ariaClick" role="button" tabindex="0" @click="addressSuggestionManagement(false)">{{$t('profile_personal_data_address_suggest_discard_button')}}</span>
        </div> 
      </template>
    </OneAlert>
    
  </div>
</template>
<style lang="scss" scoped>

  .section-icon-address {
      background-image: url('~@/assets/default/light/address-default-light.svg');
      &.__d{
          background-image: url('~@/assets/default/dark/address-default-dark.svg');
      }
      &.__m {
        background-image: url('~@/assets/mini/light/address-mini-light.svg');
        &.__d{
            background-image: url('~@/assets/mini/dark/address-mini-dark.svg');
        }
      }
      &.__rr {
        background-image: url('~@/assets/default/dark/address-default-dark.svg');
      }
    }
    .address_type_radio_container{
      margin-bottom: 0.5rem;
    }
    .checkbox__mark{
      margin-top: 10px;
    }
    .checkbox-label{
      font-size: 16px;
      margin-left: 15px;
      line-height: 24px;

      @media (max-width: 768px) {
        font-size: 14px;
      }

      &.__m{
        line-height: 27px;
        font-size: 18px;
        font-weight: 400;
        font-family: MINISansSerif;

        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
      &.__t{
        line-height: inherit;
        font-family: ToyotaSemibold;
      }
      &.__alphabet{
        font-size: 16px;
        font-weight: 500;
        font-family: AlphabetSansPro-Medium;
        color: $alphabetblue;

        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }


  .discard-label {
    background-image: url('~@/assets/default/light/arrow-default-light.svg');
    font-weight: bold;
    background-repeat: no-repeat;
    padding-left: 15px;
    margin-top: 25px;
    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
    &.__d{
      background-image: url('~@/assets/default/dark/arrow-default-dark.svg');
    }
    &.__m{
      background-image: url('~@/assets/mini/light/arrow-mini-light.svg');
      &:hover {
        text-decoration: none;
      }
      &.__d{
        background-image: url('~@/assets/mini/dark/arrow-mini-dark.svg');
      }
      span{
        margin-left: 12px;
        font-size: 14px;
        line-height: 13px;
        font-weight: normal;
        font-family: MINISansSerif;
        border-bottom: 1px solid $dusty;
        &:hover {
          font-weight: bold;
        }
      }
    }
    &.__t{
       &:hover {
        text-decoration: none;
      }
      span{
        margin-left: 12px;
        font-size: 14px;
        line-height: 13px;
        font-weight: normal;
        font-family: ToyotaSemibold;
        border-bottom: 1px solid $dusty;
        &:hover{
          border-bottom: 1px solid $toyotared;
        } 
        
      } 
    }
  }

  .info {
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
    &.address {
      display: flex;
      flex-direction: column;
    }
  }

  .address-status-label {
    margin-top: 2rem;
  }

  .radio-column {
    display: flex;
    flex-direction: column;
    margin-right: 0.75rem;
  }

  .address-value-list {
    display: flex;
    flex-direction: column;
    margin-top: 3px;
    min-height: 209px;
    max-width: calc(100% - (24px + 0.75rem));
    &.business-address-value-list {
      min-height: 243px;
    }
  }

  .has-no-company-name {
    .address-value:first-child {
      margin-top: 0 !important;
    }
  }

  .radio {
    position: static !important;
  }

  .dashboardContainer {
    margin: 0 !important;
  }

  .add {
    padding-left: 0 !important;
  }
</style>
<script>
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";
import useVuelidate from '@vuelidate/core';
import { defineComponent } from 'vue';

import ProfileAddressesForm from "./ProfileAddressesForm.vue";

export default defineComponent({
  inject:['$c', '$t', 'brandClass'],
  setup() {
    return { v$: useVuelidate() }
  },
  name: "ProfileAddresses",
  //validations from vuelidate
  mixins: [oneUtils.customValidationMixin,oneUtils.accessibilityMixin],
  props: {
    user: {},
    editDisabled: {
      default: false,
      type: Boolean,
    },
  },

  components:{
    ProfileAddressesForm
  },
  updated(){
    this.$emit('isEditMode', {subSection:'PA', value:this.editMode !== this.ACTION.VIEW});
  },
  data: () => ({
    ACTION: oneUtils.enums.actionMode,
    form: {
      type: null,
      street: "",
      houseNumber: null,
      postalCode: null,
      city: null,
      country: null,
      preferred: null,
      strSuppl1: null,
      strSuppl2: null,
      regionCode: null,
      companyName: null,
      postOfficeBox: null,
    },
    fastCompletionResults:{},
    typeIndex: null,
    // countryIndex: null,
    // showSelect: false,
    editMode: null,
    indexToEdit: null,
    showPopup: false,
    indexToRemove: -1,
    typeToRemove: "PRIVATE",
    isPreferredToRemove: false,
    suggestedAddress: {},
    isSuggestedAddressModalVisible: false,
    clickedElementId: null,
    regionCodesLabelsPerCurrentLanguage: {}
  }),
  computed: {
    sectionConfig() {
      return this.$c("_profileConfiguration").addressesSection;
    },
    selectedOptions: function(){
      return this.getSelectOptions('list_of_countries');
    },
    typeKeys() {
      return this.$c("list_of_addressStatus").map(({ typeKey }) => typeKey);
    },
  },
  mounted() {
    if(this.$c('show_personal_addresses')){
      this.prepareAddressesForTransparentMigration();
    }

    //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code

    const countries = [];

    this.userAddresses().forEach((userAddress) => {
      if(countries.indexOf(userAddress.country) === -1) {
        countries.push(userAddress.country);
      }
      
    });

    countries.forEach((country) => {
      oneUtils.utils.getRegionCodesFromServer(country).then((regionCodes)=>{
        //regions for selected language
        this.regionCodesLabelsPerCurrentLanguage[country] = oneUtils.utils.getRegionCodesByCurrentLanguage(regionCodes);
        
      })
    })
  },
  methods: {
    isRegionCodeVisible(country) {
      return !this.sectionConfig.addressesSectionByCountrySelection?.[country]?.hide_region_code;
    },
    getDisplayTogether(country) {
      if (this.sectionConfig.addressesSectionByCountrySelection &&
        this.sectionConfig.addressesSectionByCountrySelection[country]) {
          return this.sectionConfig.addressesSectionByCountrySelection[country].displayTogether;
      }
      return this.sectionConfig.displayTogether;
    },
    getFieldOrder(fieldName, country) {
      if (this.sectionConfig.addressesSectionByCountrySelection &&
        this.sectionConfig.addressesSectionByCountrySelection[country] &&
        this.sectionConfig.addressesSectionByCountrySelection[country][fieldName]) {
        return this.getCountrySpecificOrder(fieldName, "addressesSectionByCountrySelection", country);
      }
      return this.getOrder(fieldName);
    },
    getFirstAddress(addresses) {
      if(addresses.length > 0) {
        const addressToShow = addresses.find((address) => {
          return !!address.userAddress.preferredFor;
        })
        return addressToShow ? [addressToShow] : [];
      } 
      return [];
    },
    /**
     * TODO: doc
     */
    prepareAddressesForTransparentMigration() {
      const userAddressStatuses = this.getUserAddressStatuses();

      // For each status
      userAddressStatuses.forEach(userAddressStatus => {

        // If there are addresses in the current status
        if(userAddressStatus.userFilteredAddresses.length) {

          // Get list of preferred addresses in the current status
          const listOfPreferredFors = userAddressStatus.userFilteredAddresses.filter(userFilteredAddress => userFilteredAddress.userAddress.preferredFor == userAddressStatus.key);

          // If there's no preferred for...
          if(!listOfPreferredFors.length) {

            // Get the first old "preferred" address in the current status
            const oldPreferredAddress = userAddressStatus.userFilteredAddresses.find(userFilteredAddress => userFilteredAddress.userAddress.preferred);
            
            // If the first old "preferred" address was found...
            if(oldPreferredAddress) {

              // Set it as the new "preferred for" field
              oldPreferredAddress.userAddress.preferredFor = userAddressStatus.key;
            }
            // Else, there's no old "preferred" address...
          }

          // For each address in the current address status, sync the old "preferred" field to the current "preferred for" status (cleaning old data)
          /* userAddressStatus.userFilteredAddresses.forEach(userFilteredAddress => {
            if(!userFilteredAddress.userAddress.preferredFor) {
              userFilteredAddress.userAddress.preferred = false;
            }
          }); */

          // TODO: looks like this doesn't work
          /* userAddressStatus.userFilteredAddresses = userAddressStatus.userFilteredAddresses.sort((a, b) => {
            if(a.userAddress.preferredFor == userAddressStatus.key) return -1;
            if(b.userAddress.preferredFor == userAddressStatus.key) return 1;
            if(a.userAddress.preferred) return -1;
            if(b.userAddress.preferred) return 1;
            if(!a.userAddress.updatedAt && !b.userAddress.updatedAt) return 0;
            if(!a.userAddress.updatedAt) return 1;
            if(!b.userAddress.updatedAt) return -1;
            if(a.userAddress.updatedAt < b.userAddress.updatedAt) return 1;
            if(a.userAddress.updatedAt > b.userAddress.updatedAt) return -1;
            return 0;
          }); */
        }
      });

      this.$emit('autosave');
    },
    // TODO: docs
    sortAddressesForTransparentMigration(userFilteredAddresses) {
      userFilteredAddresses = userFilteredAddresses.sort((a, b) => {
        if(!a.userAddress.updatedAt && !b.userAddress.updatedAt) return 0;
        if(!a.userAddress.updatedAt) return 1;
        if(!b.userAddress.updatedAt) return -1;
        if(a.userAddress.updatedAt < b.userAddress.updatedAt) return 1;
        if(a.userAddress.updatedAt > b.userAddress.updatedAt) return -1;
        return 0;
      });

      return userFilteredAddresses;
    },
    /**
     * Tracy tracking for add address cancelled and edit management
     */
    cancelClicked(){
      this.handleEditMode(null, undefined, this.clickedElementId);
    },
    /**
     * Edit address management 
     */
    handleEditMode(key, type = undefined, clickedElementId = null) {
      this.editMode = key;
      this.indexToEdit = key;
      this.clickedElementId = clickedElementId;
      if (this.editMode !== this.ACTION.VIEW && this.editMode !== this.ACTION.ADD) {
        this.prefillForm(key);
      } else {
        this.form = {
          type: type || this.typeKeys[0],
          street: null,
          houseNumber: null,
          postalCode: null,
          city: null,
          country: null,
          preferred: false,
          strSuppl1: null,
          strSuppl2: null,
          regionCode: null,
        };
        this.v$.$reset();

        // prefill country based on parameters - start
        const countryToFind = this.$store.state.configurationParams.country;
        const country = this.selectedOptions.find(c => c.value === countryToFind);
        this.form.country = country && country.value;
        this.touchForm("country");
        // prefill country based on parameters - end
        
        // Use prosime.resolve() to set focus on the previously clicked element, because previously clicked element is hidden during edit / add mode
        if(this.editMode === this.ACTION.VIEW) {
          console.log(this.clickedElementId, document.getElementById(this.clickedElementId));
          Promise.resolve().then(() => {
            document.getElementById(this.clickedElementId)?.focus();
            this.clickedElementId = null;
          });
        }
      }
      if (this.editMode !== this.ACTION.VIEW) {
        setTimeout(() => {
          this.sortAddressInputs();
          
        }, 100);
      }
    },
    /**
     *  Sorting the address and scroll to the add section
     */
    sortAddressInputs(){
      const el = document.querySelector('#address-form-input');
      if(el && el.children){
        const list = Array.prototype.slice.call(el.children, 0);
        if(list[0] && list[0].parentNode){
          const father = list[0].parentNode;
          list.sort((a,b)=>{
            const first = Number(a.style?.order);
            const second = Number(b.style?.order);
            if (first<second) {
              return -1;
            }
            if (first>second) {
              return 1;
            }
            return 0;
          });
          this.sortInDom(list, father);
        }
      }
      // const element = this.$refs['add'];
      /* const element = document.getElementById('add');
      if(element){
        element.scrollIntoView({behavior: "smooth", block: "start"});
      } */

      oneUtils.utils.scrollTo("address-form-container");
    },
    /**
     *  Sorting list's element and adding to the DOM
     */
    sortInDom(list, father){
      for (const elem of list) {
        elem.remove();
      }
      for (const elem of list) {
        father.appendChild(elem)
      }
    },
    /**
     *  Return lis of countrues key
     */
    countryLabel(key) {
      return this.getSelectLabelByValue("list_of_countries", key);
    },

    //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code
    regionLabel(country, key) {
      if(!this.regionCodesLabelsPerCurrentLanguage[country]) return key;
      return this.regionCodesLabelsPerCurrentLanguage[country].find(rc => {
        if(country === "JP") {
          return rc.value === key || ("JP " + rc.label === key);
        }

        if(country === "AU") {
          return rc.value === key || ("AU " + rc.value === key);
        }

        return rc.value === key
      })?.label ?? key;
    },
    /**
     *  Return lis of address status key
     */
    statusLabel(key) {
      return this.getSelectLabelByValue("list_of_addressStatus", key);
    },
    /**
     * Returns the list of available address statuses, excluding the provided statuses
     */
    getAddressStatuses(excludedAddressStatuses = []) {
      let addressStatuses = [];

      addressStatuses = this.$c("list_of_addressStatus").filter(addressStatus => 
        !excludedAddressStatuses.includes(addressStatus.typeKey)
      );

      return addressStatuses;
    },
    /**
     *  Return user's addresses if exists
     */
    userAddresses: function () {
      return this.user.addresses || [];
    },
    /**
     * Return user's addresses, filtered according to the provided address status
     */
    getUserFilteredAddresses(addressStatus) {
      const userAddresses = this.userAddresses();

      let filteredUserAddresses = [];
      
      userAddresses.forEach((userAddress, userAddressIndex) => {
        if(userAddress.addressStatus == addressStatus) {
          let filteredUserAddress = {
            userAddress: userAddress,
            userAddressIndex: userAddressIndex
          }

          filteredUserAddresses.push(filteredUserAddress);
        }
      });

      return filteredUserAddresses;
    },
    /**
     * Returns the list of the user's phones for each avalable phone typology, excluding the provided phone statuses and phone types
     */
    getUserAddressStatuses(excludedAddressStatuses) {
      const addressStatuses = this.getAddressStatuses(excludedAddressStatuses);

      let userAddressStatuses = [];

      addressStatuses.forEach(addressStatus => {
        let userAddressStatus = {
          ...addressStatus,
          userFilteredAddresses: this.getUserFilteredAddresses(addressStatus.key)
        }

        userAddressStatuses.push(userAddressStatus);
      });

      return userAddressStatuses;
    },
    /**
     *  Hide delete address popup and call the submitUpdate function
     */
    remove: function () {
      this.showPopup = false;

      document.getElementById(this.clickedElementId)?.focus();
      this.clickedElementId = null;

      this.submitUpdate();
    },
    /**
     *  Get the index of address to remove and show the address remove popup
     */
    openPopup(filteredAddressObject, clickedElementId) {
      this.indexToRemove = filteredAddressObject.userAddressIndex;
      /* this.form.type = filteredAddressObject.userAddress.addressStatus;
      this.form.preferred = filteredAddressObject.userAddress.preferred; */
      this.typeToRemove = filteredAddressObject.userAddress.addressStatus;
      this.isPreferredToRemove = filteredAddressObject.userAddress.preferredFor ? true : false;
      this.clickedElementId = clickedElementId;


      this.showPopup = true;
    },
    /**
     *  Close the address remove popup
     */
    closePopup() {
      this.showPopup = false;
      
      document.getElementById(this.clickedElementId)?.focus();
      this.clickedElementId = null;
    },
    isAddButtonVisible(userAddressStatus) {
      return !this.editDisabled &&
        this.getFirstAddress(userAddressStatus.userFilteredAddresses).length === 0 &&
        (
          this.editMode == this.ACTION.VIEW || 
          userAddressStatus.key != this.form.type
        );
    },
    /**
     *  Show the suggest address popup
     */
    presentSuggestedAddressModal(suggestAddress) {
      this.suggestedAddress = suggestAddress;
      this.isSuggestedAddressModalVisible = true;
      oneUtils.utils.hideGlobalLoader();
    },
    /**
     *  Hide the suggest address popup
     */
    dismissSuggestedAddressModal() {
      this.isSuggestedAddressModalVisible = false;
      this.suggestAddress = {};
      this.handleEditMode(null, undefined, this.clickedElementId);
    },
    /**
     *  Get address value from the suggested address object
     */
    prepareCurrentAddress(currentAddress, country, companyName, postOfficeBox){
      if(typeof this.suggestedAddress.street != 'undefined') currentAddress.street = this.suggestedAddress.street;
      if(typeof this.suggestedAddress.houseNumber != 'undefined') currentAddress.houseNumber = this.suggestedAddress.houseNumber;
      if(typeof this.suggestedAddress.postalCode != 'undefined') currentAddress.postalCode = this.suggestedAddress.postalCode;
      if(typeof this.suggestedAddress.city != 'undefined') currentAddress.city = this.suggestedAddress.city;
      if(typeof this.suggestedAddress.strSuppl1 != 'undefined') currentAddress.strSuppl1 = this.suggestedAddress.strSuppl1;
      if(typeof this.suggestedAddress.strSuppl2 != 'undefined') currentAddress.strSuppl2 = this.suggestedAddress.strSuppl2;
      if(typeof this.suggestedAddress.regionCode != 'undefined') currentAddress.regionCode = this.suggestedAddress.regionCode;
      currentAddress.country = country ? country.key : null;
      if(typeof this.form.type != 'undefined') currentAddress.addressStatus = this.form.type;
      // if(typeof this.form.preferred != 'undefined') currentAddress.preferred = this.form.preferred;
      if(typeof this.form.preferred != 'undefined' && this.form.preferred) currentAddress.preferredFor = this.form.type;
      if(typeof companyName != 'undefined')currentAddress.companyName = companyName;
      if(typeof postOfficeBox != 'undefined')currentAddress.postOfficeBox = postOfficeBox;
    },
    /**
     *  Get address value
     */
    prepareCurrentAddressFromForm(currentAddress, country, companyName, postOfficeBox){
      if(typeof this.form.street != 'undefined') currentAddress.street = this.form.street;
      if(typeof this.form.houseNumber != 'undefined') currentAddress.houseNumber = this.form.houseNumber;
      if(typeof this.form.postalCode != 'undefined') currentAddress.postalCode = this.form.postalCode;
      if(typeof this.form.city != 'undefined') currentAddress.city = this.form.city;
      if(typeof this.form.strSuppl1 != 'undefined') currentAddress.strSuppl1 = this.form.strSuppl1;
      if(typeof this.form.strSuppl2 != 'undefined') currentAddress.strSuppl2 = this.form.strSuppl2;
      if(typeof this.form.regionCode != 'undefined') currentAddress.regionCode = this.form.regionCode;
      currentAddress.country = country ? country.key : null;
      if(typeof this.form.type != 'undefined') currentAddress.addressStatus = this.form.type;
      // if(typeof this.form.preferred != 'undefined') currentAddress.preferred = this.form.preferred;
      if(typeof this.form.preferred != 'undefined' && this.form.preferred) currentAddress.preferredFor = this.form.type;
      if(typeof companyName != 'undefined') currentAddress.companyName = companyName;
      if(typeof postOfficeBox != 'undefined') currentAddress.postOfficeBox = postOfficeBox;
    },

    /**
     *  Apply or discard address suggestion
    */
    addressSuggestionManagement(suggestApproved) {
      let currentAddress = this.user.addresses[this.indexToEdit];
      const country = this.$c("list_of_countries").find(( country ) => country.key === this.form.country);
      const companyName = this.form.type!="BUSINESS" ? undefined : this.form.companyName;
      const postOfficeBox = this.form.type!="BUSINESS" ? undefined : this.form.postOfficeBox;

        if (!currentAddress) {
            const newAddress = this.newAddressValorization(suggestApproved, country, companyName, postOfficeBox);
            this.user.addresses.push(newAddress);
        } else {
          if (suggestApproved) {
                currentAddress = this.prepareCurrentAddress(currentAddress,country, companyName, postOfficeBox);
          } else {
                currentAddress = this.prepareCurrentAddressFromForm(currentAddress, country, companyName, postOfficeBox);
          }
        }
        this.dismissSuggestedAddressModal();
    },

      /**
       *  valorizing address with suggested value or with form value
      */
      newAddressValorization(suggestApproved, country, companyName, postOfficeBox) {
        const filteredOtherTypeAddressesObjects = this.getUserFilteredAddresses(this.form.type === "PRIVATE" ? "BUSINESS" : "PRIVATE");

        const isPreferred = !filteredOtherTypeAddressesObjects.find(filteredAddressObject => 
          filteredAddressObject.userAddress.preferred
        );

        let address = {
          "street": suggestApproved ? this.suggestedAddress.street : this.form.street,
          "houseNumber": suggestApproved ? this.suggestedAddress.houseNumber: this.form.houseNumber,
          "postalCode": suggestApproved ? this.suggestedAddress.postalCode : this.form.postalCode,
          "city": suggestApproved ? this.suggestedAddress.city : this.form.city,
          "strSuppl1": suggestApproved ? this.suggestedAddress.strSuppl1: this.form.strSuppl1,
          "strSuppl2": suggestApproved ? this.suggestedAddress.strSuppl2: this.form.strSuppl2,
          "regionCode": suggestApproved ? this.suggestedAddress.regionCode: this.form.regionCode,
          "country": country ? country.key : null,
          "addressStatus": this.form.type,
          "preferredFor": this.form.type,
          "preferred": isPreferred,
          "companyName": companyName,
          "postOfficeBox": postOfficeBox
        };

        if(this.form.preferred) {
          address.preferredFor = this.form.type;
        }

        return address;
      },

    /**
     *  Form valorization
    */
    prefillForm: function (key) {
      if (this.user.addresses && this.typeKeys.includes(this.user.addresses[key].addressStatus)) {
        this.form.type=this.user.addresses[key].addressStatus;
        this.touchForm("type");
      }
      if (this.user.addresses && this.selectedOptions.some(c => c.value === this.user.addresses[key].country)) {
        const country = this.selectedOptions.find(c => c.value === this.user.addresses[key].country);
        this.form.country = country.value;
        this.touchForm("country");
      }

      this.form.street = this.user.addresses[key].street;
      this.touchForm("street");
      this.form.houseNumber = this.user.addresses[key].houseNumber;
      this.touchForm("houseNumber");
      this.form.postalCode = this.user.addresses[key].postalCode;
      this.touchForm("postalCode");
      this.form.city = this.user.addresses[key].city;
      this.touchForm("city");
      this.form.strSuppl1 = this.user.addresses[key].strSuppl1;
      this.touchForm("strSuppl1");
      this.form.strSuppl2 = this.user.addresses[key].strSuppl2;
      this.touchForm("strSuppl2");
      this.form.regionCode = this.user.addresses[key].regionCode;
      this.touchForm("regionCode");
      // this.form.preferred = this.user.addresses[key].preferred;

      this.form.preferred = !!this.user.addresses[key].preferredFor;
      this.touchForm("preferred");
      

      this.form.companyName = this.user.addresses[key].companyName;
      this.touchForm("companyName");
      this.form.postOfficeBox = this.user.addresses[key].postOfficeBox;
      this.touchForm("postOfficeBox");

    },

    /**
     *  Form submit
    */
    validateAddress(regionCodesFromServerPerCurrentLanguage, country) {
      this.submitUpdate();
      //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code
      this.regionCodesLabelsPerCurrentLanguage[country] = regionCodesFromServerPerCurrentLanguage;
    },

    /**
     *  Form submit
    */
    getSuggestAddress(address) {
      return oneUtils.httpClient.suggestAddress(address, oneUtils.enums.tolerantProfileKey.INTERACTIVE, this.sectionConfig);
    },

    /**
     *  Address valorization with edited address
    */
    prepareEditAddress(oldAddress, country){
      let fields = [
        "street",
        "houseNumber",
        "postalCode",
        "city",
        "strSuppl1",
        "strSuppl2",
        "regionCode"
      ];

      // Standard cases

      fields.forEach(field => {
        if(typeof this.form[field] != 'undefined') {
          oldAddress[field] = this.form[field];
        }
      });

      // Other cases

      oldAddress = this.prepareEditAddressOtherCases(oldAddress, country);

      return oldAddress;
    },

    prepareEditAddressOtherCases(oldAddress, country) {
      if(typeof this.form.preferred != 'undefined') {
        if(this.form.preferred) {
          oldAddress.preferredFor = this.form.type;
        }
      }

      if(typeof this.form.type != 'undefined') {
        oldAddress.addressStatus = this.form.type
      };

      oldAddress = this.prepareEditAddressOtherCasesCompanyName(oldAddress);

      oldAddress = this.prepareEditAddressOtherCasesPostOfficeBox(oldAddress);

      oldAddress = this.prepareEditAddressOtherCasesCountry(oldAddress, country);

      return oldAddress;
    },

    prepareEditAddressOtherCasesCompanyName(oldAddress) {
      if(typeof this.form.companyName != 'undefined') {
        if(this.form.type!="BUSINESS") {
          oldAddress.companyName = '';
        }
        else {
          oldAddress.companyName = this.form.companyName;
        }
      }

      return oldAddress;
    },

    prepareEditAddressOtherCasesPostOfficeBox(oldAddress) {
      if(typeof this.form.postOfficeBox != 'undefined') {
        if(this.form.type!="BUSINESS") {
          oldAddress.postOfficeBox = '';
        }
        else {
          oldAddress.postOfficeBox = this.form.postOfficeBox;
        }
      }

      return oldAddress;
    },

    prepareEditAddressOtherCasesCountry(oldAddress, country) {
      if(typeof this.form.country != 'undefined') {
        if(country) {
          oldAddress.country = country.value;
        }
        else {
          oldAddress.country = '';
        }
      }

      return oldAddress;
    },
    
    /**
     * Set the provided address as preferred
     */
    handlePreferredAddress(filteredAddressObject) {
      this.editMode = filteredAddressObject.userAddressIndex; //todo: sistemare con enum.actions.EDIT
      this.indexToEdit = filteredAddressObject.userAddressIndex;

      this.prefillForm(this.indexToEdit);

      this.getUserAddressStatuses().forEach((userAddressStatus) => {
        userAddressStatus.userFilteredAddresses.forEach((userFilteredAddress) => {
          userFilteredAddress.userAddress.preferred = false;
        })
      })

      
      this.form.preferred = true;
      filteredAddressObject.userAddress.preferred = true;

      this.submitUpdate(true);
    },

    /**
     *  Edit management
     *  Tolerant control
    */
    async manageEdit(country, isPreferredRadioClicked){
      const filteredAddressObjects = this.getUserFilteredAddresses(this.form.type);

      if(this.form.preferred){
        for (const filteredAddressObject of this.getUserFilteredAddresses(this.form.type)) {
          // filteredAddressObject.userAddress.preferred = false;
          delete filteredAddressObject.userAddress.preferredFor;
        }
      }
      // If there are no other valid addresses in the current address status section...
      else if(!filteredAddressObjects.filter(filteredAddressObject => 
        !filteredAddressObject.userAddress.delete &&
        filteredAddressObject.userAddressIndex != this.indexToEdit
      ).length) {
        console.log("No other valid address found in the " + this.form.type + " section: setting the edited address as preferred");
        this.form.preferred = true;
      }

      if(!isPreferredRadioClicked) {
        // tolerant flow - start
        const newAddress = this.form;
        newAddress.country = country ? country.value : null;

        if(!this.isRegionCodeVisible(newAddress.country)) {
          newAddress.regionCode = "";
        }

        const tolerantEnabled = this.$c('enable_tolerant_interactive');
        if (tolerantEnabled) {
          const suggestAddress = await this.getSuggestAddress(newAddress);
          if (suggestAddress) {
            this.presentSuggestedAddressModal(suggestAddress);
            return;
          }
        }
        // tolerant flow - end
      }

      let oldAddress = this.user.addresses[this.indexToEdit];
      oldAddress = this.prepareEditAddress(oldAddress, country);
      
      this.handleEditMode(null, undefined, this.clickedElementId);
    },

    /**
     *  Add management
     *  Tracy tracking: address added with + type + preferred
     *  Tolerant control
    */
    async manageAdd(country){
      const filteredOtherTypeAddressesObjects = this.getUserFilteredAddresses(this.form.type === "PRIVATE" ? "BUSINESS" : "PRIVATE");

      const newAddress = {
        "street": this.form.street,
        "houseNumber": this.form.houseNumber,
        "postalCode": this.form.postalCode,
        "city": this.form.city,
        "strSuppl1": this.form.strSuppl1,
        "strSuppl2": this.form.strSuppl2,
        "regionCode": this.form.regionCode,
        "addressStatus": this.form.type,
        "country": country ? country.value : null,
        "preferredFor": this.form.type
      };

      // tolerant flow - start
      const tolerantEnabled = this.$c('enable_tolerant_interactive');
      if (tolerantEnabled) {
        const suggestAddress = await this.getSuggestAddress(newAddress);
        if (suggestAddress) {
          this.presentSuggestedAddressModal(suggestAddress);
          return;
        }
      }
      if(this.form.type=="BUSINESS"){
        newAddress['companyName'] = this.form.companyName;
      }

      newAddress.preferred = !filteredOtherTypeAddressesObjects.find(filteredAddressObject => 
        filteredAddressObject.userAddress.preferred
      );

      if(this.form.type=="BUSINESS"){
        newAddress['postOfficeBox'] = this.form.postOfficeBox;
      }
      // tolerant flow - end
      this.user.addresses.push(newAddress);
      this.handleEditMode(null, undefined, this.clickedElementId);
    },


    /**
     * Remove management
     */

    manageRemove(){
      // If the address to be deleted is the original user address
      if(this.$store.getters.getOriginalUser.addresses && this.$store.getters.getOriginalUser.addresses[this.indexToRemove]){

        this.user.addresses[this.indexToRemove]['delete']=true;

        if(this.user.addresses[this.indexToRemove].preferredFor) {
          delete this.user.addresses[this.indexToRemove].preferredFor;
        }

        const filteredOtherTypeAddressesObjects = this.getUserFilteredAddresses(this.user.addresses[this.indexToRemove].addressStatus === "PRIVATE" ? "BUSINESS" : "PRIVATE");

        let indexToAddPreferred;
        filteredOtherTypeAddressesObjects.forEach((filteredAddressObject) => {
          if(!!filteredAddressObject.userAddress.preferredFor) {
            indexToAddPreferred = filteredAddressObject.userAddressIndex;
          }
        }
        );
        if(indexToAddPreferred !== undefined) {
          this.user.addresses[indexToAddPreferred].preferred = true;
        }


        const newPreferredForAddress = this.getUserFilteredAddresses(this.user.addresses[this.indexToRemove].addressStatus).toSorted((a, b) => {
          return oneUtils.httpClient.sortAddressesUpdatedAt(a.userAddress, b.userAddress)
        }).filter((filteredAddressObject) => {
          return filteredAddressObject.userAddress?.delete === undefined;
        })

        if(newPreferredForAddress.length !== 0) {
          this.user.addresses[newPreferredForAddress[0].userAddressIndex].preferredFor = this.user.addresses[this.indexToRemove].addressStatus;
        }
        // //needed for watch
        if(this.user.addresses[this.indexToRemove]['street']){
          const street = JSON.parse(JSON.stringify(this.user.addresses[this.indexToRemove]['street']));
          this.user.addresses[this.indexToRemove]['street']="";
          this.user.addresses[this.indexToRemove]['street']=street;
        }
        if(this.user.addresses[this.indexToRemove]['addressStatus']){
          const addressStatus = JSON.parse(JSON.stringify(this.user.addresses[this.indexToRemove]['addressStatus']));
          this.user.addresses[this.indexToRemove]['addressStatus']="";
          this.user.addresses[this.indexToRemove]['addressStatus']=addressStatus;
        }
      } else{
        this.user.addresses.splice(this.indexToRemove, 1);
      }
    },


    /**
     * Form submit management for add, edit or delete address
     */

    async submitUpdate(isPreferredRadioClicked) {
      const addressStatus = this.form.type;

      if(!isPreferredRadioClicked) {
        oneUtils.utils.showGlobalLoader();
      }

      if(!this.user.addresses){
        this.user.addresses = [];
      }

      const country = this.selectedOptions.find(c => c.value === this.form.country);
      if (this.user.addresses[this.indexToEdit]){
        //edit
        await this.manageEdit(country, isPreferredRadioClicked);

        if(!this.form.preferred) {
          this.resetStatusPreferredAddress(addressStatus, this.indexToEdit);
        }
      } else if(this.editMode === this.ACTION.ADD){
        //add
        await this.manageAdd(country);
      }else{
        //remove
        this.manageRemove();

        //if(this.isPreferredToRemove) {
        //  this.resetStatusPreferredAddress(this.typeToRemove);
        //}
      }

      if(!isPreferredRadioClicked) {
        oneUtils.utils.hideGlobalLoader();
      }
    },

    resetStatusPreferredAddress(addressStatus, excludedAddressIndex = -1) {
      const undeletedPreferredAddresses = this.userAddresses().filter((address, addressIndex) => 
        address.addressStatus == addressStatus && 
        !address.delete && 
        // address.preferred &&
        address.preferredFor &&
        addressIndex != excludedAddressIndex
      );

      console.log("Undeleted and preferred addresses in the " + addressStatus + " section, with index different than " + excludedAddressIndex + ": ", undeletedPreferredAddresses);

      // If user has no preferred address in the address status section they just removed an address from...
      if(!undeletedPreferredAddresses.length) {

        console.log("I'm resetting the preferred address for the " + addressStatus + " section");

        // Find the first address in the address status section they just removed an address from and set it as preferred
        let userAddressToSetAsNewPreferred = this.userAddresses().find(address => 
          address.addressStatus == addressStatus && 
          !address.delete
        );
        
        if(userAddressToSetAsNewPreferred) {
          // userAddressToSetAsNewPreferred.preferred = true;
          userAddressToSetAsNewPreferred.preferredFor = addressStatus;
        }
      }
    },

    displayUserAddressData(userAddress, fieldName) {
      if(!this.getDisplayTogether(userAddress.country)) {
        let toDisplay = userAddress[fieldName];
        if(fieldName === "country") {
          toDisplay = this.countryLabel(userAddress[fieldName]);
        }
        //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code
        if(fieldName === "regionCode") {
          toDisplay = this.regionLabel(userAddress.country, userAddress[fieldName]);
        }
        return toDisplay;
      } 

      for (const [key, fieldList] of Object.entries(this.getDisplayTogether(userAddress.country))) {
        if(key === fieldName) {
          let dataToReturnAsArray = [];

          let toDisplay = userAddress[fieldName];
          if(fieldName === "country") {
            toDisplay = this.countryLabel(userAddress[fieldName]);
          }
          //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code
          if(fieldName === "regionCode") {
            toDisplay = this.regionLabel(userAddress.country, userAddress[fieldName]);
          }

          dataToReturnAsArray.push({
            fieldName: toDisplay,
            order: 0
          });

          for (const [fieldKey, config] of Object.entries(fieldList)) { 
            if(userAddress[fieldKey] && config.display) {

              let toDisplay = userAddress[fieldKey];
              if(fieldKey === "country") {
                toDisplay = this.countryLabel(userAddress[fieldKey]);
              }
              //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code
              if(fieldKey === "regionCode") {
                toDisplay = this.regionLabel(userAddress.country, userAddress[fieldKey]);
              }

              dataToReturnAsArray.push({
                fieldName: toDisplay,
                order: config.order
              });
            }
          }
      
          return dataToReturnAsArray.sort((a, b) => {
            return a.order < b.order ? -1 : 1;
          }).map((arr) => {
            return arr.fieldName;
          }).join(" ");
        } else if (fieldList[fieldName] && fieldList[fieldName].display) {
          return "";
        }
      }

      let toDisplay = userAddress[fieldName];
        if(fieldName === "country") {
          toDisplay = this.countryLabel(userAddress[fieldName]);
        }
        //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code
        if(fieldName === "regionCode") {
          toDisplay = this.regionLabel(userAddress.country, userAddress[fieldName]);
        }
      
      return toDisplay;
    },

    isUserAddressDataToDisplay(userAddress, fieldName) {
      if(!userAddress[fieldName]) {
        if(!this.getDisplayTogether(userAddress.country) || !this.getDisplayTogether(userAddress.country)[fieldName]) return false;

        let areDisplayTogetherFieldNotEmpty = false; 
        
        for (let flName of Object.keys(this.getDisplayTogether(userAddress.country)[fieldName])) {
          if(userAddress[flName] && this.getDisplayTogether(userAddress.country)[fieldName][flName]?.display) {
            areDisplayTogetherFieldNotEmpty = true;
            break;
          }
        }

        if(!areDisplayTogetherFieldNotEmpty) return false;
      }

      if(fieldName === "regionCode") {
        return this.isRegionCodeVisible(userAddress.country);
      }
      
      if(!this.getDisplayTogether(userAddress.country)) return true;

      for (const [key, fieldList] of Object.entries(this.getDisplayTogether(userAddress.country))) {
        if(key === fieldName) {
          for(const [_, innerFieldConfig] of Object.entries(fieldList)) {
            if(!innerFieldConfig.display) return false;
          }
          return true;
        } else if (fieldList[fieldName] && fieldList[fieldName].display) {
          return false;
        }
      }
      return true;
    },

  }
});
</script>
